import { notifications, notificationsStore } from '@mantine/notifications';
import { useCallback } from 'react';

import { NotifierType } from './notifier.types';

import type { NotifierContextType, NotifierProps } from './notifier.types';

const NOTIFIER_COLOR_MAP: Record<string, string> = {
  [NotifierType.SUCCESS]: 'green',
  [NotifierType.ERROR]: 'red',
  [NotifierType.WARNING]: 'yellow',
  [NotifierType.INFO]: 'blue',
};

const DEFAULT_DURATION_SECONDS = 3;

// The notification that notify() showed last. Every useNotifier caller shares it, so a new
// notify() replaces the previous one wherever it came from.
let lastNotificationId: string | null = null;

// Removes notify()'s last notification and leaves the rest on screen, such as the session
// warnings that App.tsx mounts. Like notifications.clean(), it does not call onClose.
const removeLastNotification = (): void => {
  const id = lastNotificationId;

  if (!id) {
    return;
  }

  lastNotificationId = null;
  notifications.updateState(notificationsStore, (current) =>
    current.filter((notification) => notification.id !== id),
  );
};

export function useNotifier(): NotifierContextType {
  const notify = useCallback((props: NotifierProps): void => {
    // Calling notify with undefined message dismisses the current notification
    // (matches klue-ui behavior for dismissing)
    if (props.message === undefined && !props.children) {
      removeLastNotification();
      return;
    }

    // Remove the previous notification first to match klue-ui's
    // single-notification-at-a-time behavior
    removeLastNotification();

    const durationMs = (props.duration ?? DEFAULT_DURATION_SECONDS) * 1000;

    lastNotificationId = notifications.show({
      title: props.children ? props.message : undefined,
      message: props.children ?? props.message ?? '',
      color:
        props.background ??
        (props.type ? NOTIFIER_COLOR_MAP[props.type] : undefined),
      icon: props.icon,
      autoClose: durationMs,
      withCloseButton: props.withCloseButton,
      onClose: props.onClose ? () => props.onClose?.() : undefined,
    });
  }, []);

  return { notify };
}
